import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import '../../styles/PropertiesScreen.css';

const AddServiceModal = ({ isOpen, onClose, property, onSave }) => {
    const [products, setProducts] = useState([]);
    const [activeProductIds, setActiveProductIds] = useState([]);
    const [selectedServices, setSelectedServices] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedServices({});
            setError('');
            setIsLoading(true);

            const fetchData = async () => {
                try {
                    const [productData, existingSubs] = await Promise.all([
                        api.getProducts(),
                        api.getPropertySubscriptions(property.id)
                    ]);
                    
                    setProducts(productData);
                    setActiveProductIds(existingSubs.map(sub => sub.product_id));
                } catch (err) {
                    setError('Failed to load required data.');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchData();
        }
    }, [isOpen, property.id]);

    const handleCheckboxChange = (productId) => {
        const today = new Date().toISOString().split('T')[0];
        setSelectedServices(prev => ({
            ...prev,
            [productId]: prev[productId] ? false : { domainName: '', startDate: today },
        }));
    };

    const handleDateChange = (productId, startDate) => {
        setSelectedServices(prev => ({
            ...prev,
            [productId]: { ...prev[productId], startDate },
        }));
    };

    const handleDomainChange = (productId, domainName) => {
        setSelectedServices(prev => ({
            ...prev,
            [productId]: { ...prev[productId], domainName },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const servicesToSubmit = Object.entries(selectedServices)
            .filter(([, value]) => value !== false)
            .map(([productId, data]) => ({
                productId: parseInt(productId),
                startDate: data.startDate,
                domainName: data.domainName || null,
            }));

        if (servicesToSubmit.length === 0) {
            setError('You must select at least one service.');
            return;
        }

        setIsSubmitting(true);
        try {
            await onSave({
                propertyId: property.id,
                services: servicesToSubmit,
            });
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to add services.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatPrice = (price) => `$${parseFloat(price).toFixed(2)}`;
    
    const formatBillingCycle = (cycle) => cycle === 'ANNUALLY' ? '/year' : '/month';

    const selectedCount = Object.values(selectedServices).filter(v => v !== false).length;

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content modal-content-service">
                <div className="modal-header">
                    <h2>Add Services</h2>
                    <p className="modal-subtitle">for {property.name}</p>
                </div>
                
                {isLoading ? (
                    <div className="modal-loading">
                        <div className="spinner"></div>
                        <p>Loading services...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {error && <div className="form-error-banner">{error}</div>}
                        
                        <div className="service-list">
                            {products.map(product => {
                                const isSubscribed = activeProductIds.includes(product.id);
                                const isSelected = !!selectedServices[product.id];
                                
                                return (
                                    <div 
                                        key={product.id} 
                                        className={`service-card ${isSubscribed ? 'subscribed' : ''} ${isSelected ? 'selected' : ''}`}
                                    >
                                        <div className="service-card-header" onClick={() => !isSubscribed && handleCheckboxChange(product.id)}>
                                            <div className="service-card-info">
                                                <span className="service-card-name">{product.name}</span>
                                                <span className="service-card-type">{product.type === 'DOMAIN' ? 'Domain Registration' : 'SaaS Subscription'}</span>
                                            </div>
                                            <div className="service-card-price">
                                                <span className="price-amount">{formatPrice(product.price)}</span>
                                                <span className="price-cycle">{formatBillingCycle(product.billing_cycle)}</span>
                                            </div>
                                        </div>
                                        
                                        {isSubscribed ? (
                                            <div className="service-card-badge">
                                                <span className="badge-subscribed">Already Subscribed</span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="service-card-details">
                                                    {parseFloat(product.one_time_setup_fee) > 0 && (
                                                        <span className="setup-fee">Setup fee: {formatPrice(product.one_time_setup_fee)}</span>
                                                    )}
                                                </div>
                                                
                                                {isSelected && (
                                                    <div className="service-card-inputs">
                                                        <div className="input-group">
                                                            <label className="input-label">Start Date</label>
                                                            <input 
                                                                type="date"
                                                                className="form-input"
                                                                value={selectedServices[product.id].startDate}
                                                                onChange={e => handleDateChange(product.id, e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        {product.type === 'DOMAIN' && (
                                                            <div className="input-group">
                                                                <label className="input-label">Domain Name</label>
                                                                <input 
                                                                    type="text"
                                                                    className="form-input"
                                                                    placeholder="e.g., example.com"
                                                                    value={selectedServices[product.id].domainName}
                                                                    onChange={e => handleDomainChange(product.id, e.target.value)}
                                                                    required
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {selectedCount > 0 && (
                            <div className="selection-summary">
                                {selectedCount} service{selectedCount > 1 ? 's' : ''} selected — Invoice will be created automatically
                            </div>
                        )}

                        <div className="modal-actions">
                            <button type="button" className="button button-secondary" onClick={onClose} disabled={isSubmitting}>
                                Cancel
                            </button>
                            <button type="submit" className="button action-button" disabled={isSubmitting || isLoading || selectedCount === 0}>
                                {isSubmitting ? 'Saving...' : `Add ${selectedCount > 0 ? selectedCount : ''} Service${selectedCount !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AddServiceModal;
